import { useMemo, useState, useRef, useEffect } from "react";
import { useLeadContext } from "../../../contexts/LeadContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useWhatsAppAccountCheck } from "../../../hooks/useWhatsAppEnabled";
import RichTextEditor from "../../../ui/inputs/RichTextEditor";
import styles from "../../../styles/components/Sections.module.css";

import googleLogo from "../../../assets/icons/google.svg";
import microsoftLogo from "../../../assets/icons/microsoft.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash.svg";
import { ReactComponent as WhatsAppIcon } from "../../../assets/icons/whatsapp.svg";

import BaseInput from "../../../ui/inputs/BaseInput/BaseInput";
import Button from "../../../ui/buttons/Button";
import DropdownSelect from "../../../ui/dropdowns/DropdownSelect";
import Toggle from "../../../ui/selectors/Toggle";
import TimezoneSelect from "../../../ui/inputs/TimezoneSelect";
import BusinessHoursEditor, {
  normalizeBusinessHours,
  validateBusinessHours,
} from "../../../ui/businessHours/BusinessHoursEditor";
import AccountActionModal from "../modals/AccountActionModal";
import config from "../../../config";
import { usersService } from "../../../services/users.service";
import { accountsService } from "../../../services/accounts.service";
import MailboxWhatsApp, { useMailboxWhatsApp } from "./MailboxWhatsApp";
import { extractApiErrorMessage } from "../../../utils/apiError";

const NEW_ACCOUNT_ID = "new-mail-account";
const LINK_POLL_INTERVAL_MS = 3000;
const LINK_POLL_MAX_ATTEMPTS = 40;

const LinkedAccountsSection = () => {
  const whatsappEnabledFor = useWhatsAppAccountCheck();
  const mailboxWhatsApp = useMailboxWhatsApp(whatsappEnabledFor());
  const { auth } = useAuth();
  const currentUser = auth?.user;

  const {
    responsible,
    loadResponsible,
    accounts: contextAccounts,
  } = useLeadContext();

  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [mailCorpUsers, setMailCorpUsers] = useState({});
  const [addingUserMailCorpId, setAddingUserMailCorpId] = useState(null);
  const [newAssignmentDraft, setNewAssignmentDraft] = useState({ userId: null, isMailAccountAdmin: false, canSend: true, canReceive: true });
  const [availableAccountUsers, setAvailableAccountUsers] = useState([]);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [accountError, setAccountError] = useState(null);
  const [showHoursErrors, setShowHoursErrors] = useState(false);
  const [consentStatusByAccount, setConsentStatusByAccount] = useState({});
  const [refreshingConsentStatusId, setRefreshingConsentStatusId] = useState(null);
  const [requestingAdminConsentId, setRequestingAdminConsentId] = useState(null);
  const [pendingLinkAccountId, setPendingLinkAccountId] = useState(null);
  const [adminConsentModalState, setAdminConsentModalState] = useState({
    isOpen: false,
    accountId: null,
  });

  const panelRef = useRef(null);
  const oauthPopupRef = useRef(null);
  const oauthPopupTimerRef = useRef(null);
  const consentPollingTimerRef = useRef(null);
  const linkPollingTimerRef = useRef(null);
  const oauthContextRef = useRef(null);
  const loadResponsibleRef = useRef(loadResponsible);

  useEffect(() => {
    loadResponsibleRef.current = loadResponsible;
  }, [loadResponsible]);

  const clearOAuthPopupTimer = () => {
    if (oauthPopupTimerRef.current) {
      window.clearInterval(oauthPopupTimerRef.current);
      oauthPopupTimerRef.current = null;
    }
  };

  const closeOAuthPopup = () => {
    try {
      oauthPopupRef.current?.close();
    } catch { }
    oauthPopupRef.current = null;
  };

  const clearConsentPollingTimer = () => {
    if (consentPollingTimerRef.current) {
      window.clearInterval(consentPollingTimerRef.current);
      consentPollingTimerRef.current = null;
    }
  };

  const clearLinkPollingTimer = () => {
    if (linkPollingTimerRef.current) {
      window.clearInterval(linkPollingTimerRef.current);
      linkPollingTimerRef.current = null;
    }
  };

  const forgetLinkedAccountState = (accountId) => {
    setConsentStatusByAccount((prev) => ({ ...prev, [accountId]: null }));
    setDrafts((prev) => {
      if (prev[accountId] == null) return prev;
      const refreshedDrafts = { ...prev };
      delete refreshedDrafts[accountId];
      return refreshedDrafts;
    });
  };

  const refreshConsentStatus = async (accountId) => {
    if (!accountId) return null;
    try {
      const statusResp = await usersService.getMicrosoftAdminConsentStatus(accountId);
      const normalized = statusResp?.exists ? statusResp : null;
      setConsentStatusByAccount((prev) => ({
        ...prev,
        [accountId]: normalized,
      }));
      return normalized;
    } catch {
      setConsentStatusByAccount((prev) => ({
        ...prev,
        [accountId]: null,
      }));
      return null;
    }
  };

  const handleRefreshConsentStatus = async (accountId) => {
    if (!accountId) return;
    setRefreshingConsentStatusId(accountId);
    try {
      await refreshConsentStatus(accountId);
    } finally {
      setRefreshingConsentStatusId(null);
    }
  };

  const accounts = useMemo(() => {
    return Array.isArray(responsible) ? responsible : [];
  }, [responsible]);

  const accountOptions = useMemo(() => {
    return Array.isArray(contextAccounts) ? contextAccounts : [];
  }, [contextAccounts]);
  const expandedConsentStatus = expandedAccountId
    ? consentStatusByAccount[expandedAccountId]?.status
    : null;
  const expandedAccountIsLinked = Boolean(
    accounts.find((account) => account.id === expandedAccountId)?.oauth_account
      ?.provider_type,
  );

  const buildDraftFromAccount = (account) => ({
    name: account.name || "",
    account:
      accountOptions.find(
        (item) =>
          String(item.id) ===
          String(
            account.account_id || account.account?.id || account.account || "",
          ),
      ) || null,
    email: account.email || "",
    password: account.password || "",
    smtp: account.smtp || "",
    smtp_port: account.smtp_port || "",
    imap: account.imap || "",
    imap_port: account.imap_port || "",
    firma: account.firma || account.signature || "",
    use_bitrix: account.use_bitrix ?? false,
    timezone: account.timezone || "",
    business_hours: normalizeBusinessHours(account.business_hours),
  });

  const buildNewDraft = () => ({
    name: "",
    account: null,
    email: "",
    password: "",
    smtp: "",
    smtp_port: "",
    imap: "",
    imap_port: "",
    firma: "",
    use_bitrix: false,
    timezone: "",
    business_hours: [],
  });

  const openEditor = (account) => {
    setExpandedAccountId(account.id);
    setAddingUserMailCorpId(null);
    setAccountError(null);
    setShowHoursErrors(false);

    if (drafts[account.id] == null) {
      setDrafts((prev) => ({
        ...prev,
        [account.id]: buildDraftFromAccount(account),
      }));
    }

    if (mailCorpUsers[account.id] == null) {
      usersService
        .listMailCorpUsers(account.id)
        .then((assignments) =>
          setMailCorpUsers((prev) => ({
            ...prev,
            [account.id]: Array.isArray(assignments) ? assignments : [],
          })),
        )
        .catch(() =>
          setMailCorpUsers((prev) => ({ ...prev, [account.id]: [] })),
        );
    }

    refreshConsentStatus(account.id);
  };

  const handleToggleEditor = (account) => {
    if (expandedAccountId === account.id) {
      setExpandedAccountId(null);
      setAddingUserMailCorpId(null);
      setAccountError(null);
      setShowHoursErrors(false);
      return;
    }
    openEditor(account);
  };

  useEffect(() => {
    clearConsentPollingTimer();

    if (!expandedAccountId || expandedAccountId === NEW_ACCOUNT_ID) {
      return undefined;
    }

    if (expandedConsentStatus !== "PENDING" || expandedAccountIsLinked) {
      return undefined;
    }

    consentPollingTimerRef.current = window.setInterval(() => {
      refreshConsentStatus(expandedAccountId);
    }, 20000);

    return () => {
      clearConsentPollingTimer();
    };
  }, [expandedAccountId, expandedConsentStatus, expandedAccountIsLinked]);

  const handleOpenAddUser = async (account) => {
    setAddingUserMailCorpId(account.id);
    setNewAssignmentDraft({ userId: null, isMailAccountAdmin: false, canSend: true, canReceive: true });

    const accountId = account.account?.id ?? account.account_id ?? account.account;
    if (!accountId) return;

    setLoadingAvailableUsers(true);
    try {
      const allAccountUsers = await accountsService.listUsers(accountId);
      const assignedUserIds = new Set((mailCorpUsers[account.id] || []).map((a) => a.user_id));
      setAvailableAccountUsers(allAccountUsers.filter((u) => !assignedUserIds.has(u.id)));
    } catch {
      setAvailableAccountUsers([]);
    } finally {
      setLoadingAvailableUsers(false);
    }
  };

  const handleAddUser = async (account) => {
    if (!newAssignmentDraft.userId) return;
    setSavingAssignment(true);
    try {
      const assignment = await usersService.addMailCorpUser(account.id, {
        user_id: newAssignmentDraft.userId,
        is_mail_account_admin: newAssignmentDraft.isMailAccountAdmin,
        can_send: newAssignmentDraft.canSend,
        can_receive: newAssignmentDraft.canReceive,
      });
      setMailCorpUsers((prev) => ({
        ...prev,
        [account.id]: [...(prev[account.id] || []), assignment],
      }));
      setAddingUserMailCorpId(null);
    } catch (error) {
      console.error("Error agregando usuario:", error?.response?.data || error);
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleRemoveUser = async (mailCorpId, assignmentId) => {
    try {
      await usersService.removeMailCorpUser(mailCorpId, assignmentId);
      setMailCorpUsers((prev) => ({
        ...prev,
        [mailCorpId]: prev[mailCorpId].filter((a) => a.id !== assignmentId),
      }));
    } catch (error) {
      console.error("Error quitando usuario:", error?.response?.data || error);
    }
  };

  const handleToggleAssignmentFlag = async (mailCorpId, assignmentId, field, currentValue) => {
    setMailCorpUsers((prev) => ({
      ...prev,
      [mailCorpId]: prev[mailCorpId].map((a) =>
        a.id === assignmentId ? { ...a, [field]: !currentValue } : a,
      ),
    }));
    try {
      await usersService.updateMailCorpUserAssignment(mailCorpId, assignmentId, { [field]: !currentValue });
    } catch (error) {
      console.error("Error actualizando flag:", error?.response?.data || error);
      setMailCorpUsers((prev) => ({
        ...prev,
        [mailCorpId]: prev[mailCorpId].map((a) =>
          a.id === assignmentId ? { ...a, [field]: currentValue } : a,
        ),
      }));
    }
  };

  const handleOpenCreateAccount = () => {
    setExpandedAccountId(NEW_ACCOUNT_ID);
    setAccountError(null);
    setShowHoursErrors(false);
    setDrafts((prev) => ({
      ...prev,
      [NEW_ACCOUNT_ID]: prev[NEW_ACCOUNT_ID] || buildNewDraft(),
    }));

    const onlyAccount = accountOptions.length === 1 ? accountOptions[0] : null;
    if (onlyAccount && !drafts[NEW_ACCOUNT_ID]?.account) {
      handleAccountSelect(onlyAccount, NEW_ACCOUNT_ID, true);
    }
  };

  const handleFieldChange = (accountId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [field]: value,
      },
    }));
  };

  // Al elegir una cuenta en el alta de una casilla, precargamos su zona horaria
  // y horarios (la casilla los hereda). En edición no se propaga: la casilla ya
  // tiene su propia config y editar la cuenta no debe pisarla.
  const handleAccountSelect = async (item, draftId, isNew) => {
    handleFieldChange(draftId, "account", item);
    if (!isNew || !item?.id) return;

    const applyInheritedConfig = (source) => {
      setDrafts((prev) => ({
        ...prev,
        [draftId]: {
          ...prev[draftId],
          account: item,
          timezone: source?.timezone || "",
          business_hours: normalizeBusinessHours(source?.business_hours),
        },
      }));
    };

    // Si la cuenta del listado ya trae la config, la usamos directo;
    // si no, pedimos el detalle.
    if (item.timezone != null || item.business_hours != null) {
      applyInheritedConfig(item);
      return;
    }
    try {
      const detail = await accountsService.getDetail(item.id);
      applyInheritedConfig(detail);
    } catch (error) {
      console.error(
        "Error obteniendo la cuenta:",
        error?.response?.data || error,
      );
    }
  };

  const handleSaveAccount = async (account) => {
    setAccountError(null);
    const draft = drafts[account.id] ?? buildDraftFromAccount(account);
    const businessHours = draft.business_hours || [];
    const hoursValidation = validateBusinessHours(businessHours);

    if (!draft.timezone || !hoursValidation.valid) {
      setShowHoursErrors(true);
      setAccountError(
        !draft.timezone
          ? "La zona horaria es obligatoria."
          : hoursValidation.message,
      );
      return;
    }

    try {
      const payload = {
        name: draft.name,
        account: draft.account?.id || null,
        email: draft.email,
        smtp: draft.smtp,
        smtp_port: draft.smtp_port,
        imap: draft.imap,
        imap_port: draft.imap_port,
        firma: draft.firma,
        use_bitrix: draft.use_bitrix ?? false,
        timezone: draft.timezone,
        business_hours: businessHours,
      };
      if (draft.password) {
        payload.password = draft.password;
      }

      await usersService.updateMailCorp(account.id, payload);
      await loadResponsible();

      setShowHoursErrors(false);
      setExpandedAccountId(null);
    } catch (error) {
      setAccountError(
        extractApiErrorMessage(error) || "No se pudo guardar la cuenta.",
      );
      console.error(
        "Error al guardar la cuenta:",
        error?.response?.data || error,
      );
    }
  };

  const handleCreateAccount = async () => {
    setAccountError(null);
    const draft = drafts[NEW_ACCOUNT_ID] || buildNewDraft();
    const businessHours = draft.business_hours || [];
    const hoursValidation = validateBusinessHours(businessHours);

    if (!hoursValidation.valid) {
      setShowHoursErrors(true);
      setAccountError(hoursValidation.message);
      return;
    }

    try {
      setCreatingAccount(true);

      const payload = {
        name: draft.name,
        account: draft.account?.id || null,
        email: draft.email,
        password: draft.password,
        smtp: draft.smtp,
        smtp_port: draft.smtp_port,
        imap: draft.imap,
        imap_port: draft.imap_port,
        firma: draft.firma,
        use_bitrix: draft.use_bitrix ?? false,
        business_hours: businessHours,
      };
      // En creación con cuenta asociada el back hereda y los ignora; sin cuenta
      // sí los usa. Mandamos timezone solo si hay para no pisar el default.
      if (draft.timezone) payload.timezone = draft.timezone;

      const created = await usersService.createMailCorp(payload);
      await loadResponsible();

      setDrafts((prev) => {
        const next = { ...prev };
        delete next[NEW_ACCOUNT_ID];
        return next;
      });

      setShowHoursErrors(false);
      if (created?.id) {
        openEditor(created);
      } else {
        setExpandedAccountId(null);
      }
      return created;
    } catch (error) {
      setAccountError(
        extractApiErrorMessage(error) || "No se pudo crear la cuenta.",
      );
      console.error("Error creando cuenta:", error?.response?.data || error);
      return null;
    } finally {
      setCreatingAccount(false);
    }
  };

  const openAdminConsentModal = (accountId) => {
    if (!accountId) {
      setAccountError(
        "No se pudo identificar la casilla para solicitar consentimiento administrativo.",
      );
      return;
    }
    setAdminConsentModalState({ isOpen: true, accountId });
  };

  const closeAdminConsentModal = () => {
    if (requestingAdminConsentId) return;
    setAdminConsentModalState({ isOpen: false, accountId: null });
  };

  const requestMicrosoftAdminConsent = async (accountId, adminEmail) => {
    if (!accountId) {
      setAccountError(
        "No se pudo identificar la casilla para solicitar consentimiento administrativo.",
      );
      return false;
    }

    const normalizedAdminEmail = (adminEmail || "").trim();
    if (!normalizedAdminEmail) {
      setAccountError("El email del administrador es obligatorio.");
      return false;
    }

    try {
      setRequestingAdminConsentId(accountId);
      const response = await usersService.requestMicrosoftAdminConsent({
        mailAccountId: accountId,
        adminEmail: normalizedAdminEmail,
      });
      const masked = response?.admin_email_masked || normalizedAdminEmail;
      const manualActionRequired = Boolean(response?.manual_action_required);
      const reviewUrl = response?.review_url || "";
      const emailSent = response?.email_sent !== false;
      const tenantWarning = response?.tenant_warning || "";
      setConsentStatusByAccount((prev) => ({
        ...prev,
        [accountId]: {
          exists: true,
          status: response?.status || "PENDING",
          admin_email_masked: masked,
          detail: "Esperando aprobación administrativa.",
          manual_action_required: manualActionRequired,
          review_url: reviewUrl,
          warning: response?.warning || "",
          tenant_warning: tenantWarning,
        },
      }));
      const baseMessage = emailSent
        ? `Solicitud administrativa enviada a ${masked}. Esperá la aprobación para continuar.`
        : "No se pudo enviar el email automáticamente. Copiá el link de aprobación y compartilo con el administrador.";
      setAccountError([baseMessage, tenantWarning].filter(Boolean).join(" "));
      return true;
    } catch (error) {
      setAccountError(
        extractApiErrorMessage(error) ||
        "No se pudo enviar la solicitud de consentimiento administrativo.",
      );
      return false;
    } finally {
      setRequestingAdminConsentId(null);
    }
  };

  const handleSubmitAdminConsent = async (values) => {
    if (requestingAdminConsentId) return;
    const accountId = adminConsentModalState.accountId;
    const adminEmail = values?.admin_email || "";
    const sent = await requestMicrosoftAdminConsent(accountId, adminEmail);
    if (sent) {
      setAdminConsentModalState({ isOpen: false, accountId: null });
    }
  };

  const handleCopyReviewUrl = async (accountId) => {
    const reviewUrl = consentStatusByAccount?.[accountId]?.review_url;
    if (!reviewUrl) {
      setAccountError("No hay link de aprobación disponible para copiar.");
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(reviewUrl);
      } else {
        throw new Error("Clipboard API no disponible");
      }
      setAccountError("Link de aprobación copiado al portapapeles.");
    } catch {
      setAccountError("No se pudo copiar el link. Abrilo y compartilo manualmente.");
    }
  };

  const oauthPopupFeatures = () => {
    const width = 620;
    const height = 760;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

    return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  };

  const openOAuthPopup = (url, provider, accountId, pendingPopup = null) => {
    let popup = null;
    if (pendingPopup && !pendingPopup.closed) {
      pendingPopup.location.href = url;
      popup = pendingPopup;
    } else {
      popup = window.open(
        url,
        `${provider}_oauth_popup_${accountId}`,
        oauthPopupFeatures(),
      );
    }

    if (!popup) {
      setLoadingProvider(null);
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    oauthContextRef.current = { provider, accountId };
    oauthPopupRef.current = popup;
    setPendingLinkAccountId(accountId);
    clearOAuthPopupTimer();

    oauthPopupTimerRef.current = window.setInterval(() => {
      if (!popup || popup.closed) {
        clearOAuthPopupTimer();
        oauthPopupRef.current = null;
        setLoadingProvider(null);
        oauthContextRef.current = null;

        Promise.resolve(loadResponsibleRef.current()).catch((error) => {
          console.error(
            "Error recargando responsables tras cierre de popup OAuth:",
            error?.response?.data || error,
          );
        });

      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      clearOAuthPopupTimer();
      clearConsentPollingTimer();
      clearLinkPollingTimer();
      closeOAuthPopup();
    };
  }, []);

  useEffect(() => {
    clearLinkPollingTimer();

    if (!pendingLinkAccountId) {
      return undefined;
    }

    let remainingAttempts = LINK_POLL_MAX_ATTEMPTS;
    linkPollingTimerRef.current = window.setInterval(() => {
      remainingAttempts -= 1;
      if (remainingAttempts <= 0) {
        clearLinkPollingTimer();
        setPendingLinkAccountId(null);
        return;
      }
      Promise.resolve(loadResponsibleRef.current()).catch((error) => {
        console.error(
          "Error recargando responsables mientras se vincula la casilla:",
          error?.response?.data || error,
        );
      });
    }, LINK_POLL_INTERVAL_MS);

    return () => {
      clearLinkPollingTimer();
    };
  }, [pendingLinkAccountId]);

  useEffect(() => {
    if (!pendingLinkAccountId) return;

    const pendingAccount = accounts.find(
      (account) => account.id === pendingLinkAccountId,
    );
    if (pendingAccount?.oauth_account?.provider_type) {
      forgetLinkedAccountState(pendingLinkAccountId);
      setPendingLinkAccountId(null);
    }
  }, [accounts, pendingLinkAccountId]);

  useEffect(() => {
    const allowedOrigins = new Set([window.location.origin]);
    if (config?.apiBaseUrl) {
      try {
        allowedOrigins.add(new URL(config.apiBaseUrl).origin);
      } catch {
        // Si la URL es inválida, mantenemos solo el origen local.
      }
    }

    const onMessage = async (event) => {
      if (!allowedOrigins.has(event.origin)) return;
      if (!oauthPopupRef.current || event.source !== oauthPopupRef.current) return;

      const data = event.data;
      if (!data || typeof data !== "object") return;

      const resolvedType =
        data.type ||
        (data.error === "admin_consent_required"
          ? "mailoauth_admin_consent_required"
          : data.error === "oauth_error"
            ? "mailoauth_error"
            : null);

      if (
        ![
          "mailoauth_linked",
          "mailoauth_error",
          "mailoauth_admin_consent_required",
        ].includes(resolvedType)
      ) {
        return;
      }

      clearOAuthPopupTimer();
      closeOAuthPopup();
      setLoadingProvider(null);

      if (resolvedType === "mailoauth_linked") {
        setAccountError(null);
        const linkedAccountId = oauthContextRef.current?.accountId;
        if (linkedAccountId) {
          forgetLinkedAccountState(linkedAccountId);
        }
        oauthContextRef.current = null;
        try {
          await loadResponsibleRef.current();
        } catch (error) {
          console.error(
            "Error recargando responsables tras OAuth exitoso:",
            error?.response?.data || error,
          );
          setAccountError(
            "La casilla se vinculó, pero no se pudo refrescar la lista. Intentá recargar.",
          );
        }
        return;
      }

      if (resolvedType === "mailoauth_error") {
        const detail =
          data.message || data.detail || "No se pudo completar la conexión OAuth.";
        setPendingLinkAccountId(null);
        setAccountError(detail);
        return;
      }

      const currentContext = oauthContextRef.current;
      openAdminConsentModal(currentContext?.accountId);
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, []);

  const missingFieldsToLink = (draft) => {
    const missing = [];
    if (!draft?.name?.trim()) missing.push("el nombre");
    if (!draft?.account?.id) missing.push("la cuenta");
    if (!draft?.email?.trim()) missing.push("el email");
    if (missing.length === 0) return "";
    return `Completá ${missing.join(", ")} para vincular el correo.`;
  };

  const handleCreateAndLink = async (provider) => {
    const pendingPopup = window.open(
      "",
      `${provider}_oauth_popup_new_mailbox`,
      oauthPopupFeatures(),
    );

    const created = await handleCreateAccount();
    if (!created?.id) {
      pendingPopup?.close();
      return;
    }
    if (!pendingPopup) {
      setAccountError(
        "La casilla se creó, pero el navegador bloqueó la ventana de conexión OAuth; habilitá los popups y usá el botón de vincular.",
      );
      return;
    }

    try {
      setLoadingProvider(`${provider}-${created.id}`);
      const url = await usersService.getMailOAuthUrl({
        provider,
        account: created,
      });
      if (pendingPopup.closed) {
        setLoadingProvider(null);
        return;
      }
      openOAuthPopup(url, provider, created.id, pendingPopup);
    } catch (error) {
      pendingPopup?.close();
      setLoadingProvider(null);
      setAccountError(
        "La casilla se creó, pero no se pudo iniciar la conexión OAuth; usá el botón de vincular para reintentar.",
      );
      console.error(
        "Error al vincular el correo:",
        error?.response?.data || error,
      );
    }
  };

  const handleLinkProvider = async (provider, account) => {
    const pendingPopup = window.open(
      "",
      `${provider}_oauth_popup_${account.id}`,
      oauthPopupFeatures(),
    );
    if (!pendingPopup) {
      setAccountError(
        "El navegador bloqueó la ventana de conexión OAuth; habilitá los popups para este sitio.",
      );
      return;
    }
    try {
      setLoadingProvider(`${provider}-${account.id}`);
      const url = await usersService.getMailOAuthUrl({ provider, account });
      if (pendingPopup.closed) {
        setLoadingProvider(null);
        return;
      }
      openOAuthPopup(url, provider, account.id, pendingPopup);
    } catch (error) {
      pendingPopup?.close();
      setLoadingProvider(null);
      setAccountError(
        `No se pudo iniciar la conexión OAuth con ${provider}; probá de nuevo.`,
      );
      console.error(
        `Error al vincular ${provider}:`,
        error?.response?.data || error,
      );
    }
  };

  const handleLinkGoogle = (account) => handleLinkProvider("google", account);

  const handleLinkMicrosoft = (account) =>
    handleLinkProvider("microsoft", account);

  const handleUnlinkGoogle = async (account) => {
    try {
      setLoadingProvider(`unlink-google-${account.id}`);
      await usersService.unlinkMailOAuth(account.id, "google");
      setPendingLinkAccountId(null);
      await loadResponsible();
    } catch (error) {
      console.error(
        "Error al desvincular Google:",
        error?.response?.data || error,
      );
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleUnlinkMicrosoft = async (account) => {
    try {
      setLoadingProvider(`unlink-microsoft-${account.id}`);
      await usersService.unlinkMailOAuth(account.id, "microsoft");
      setPendingLinkAccountId(null);
      await loadResponsible();
    } catch (error) {
      console.error(
        "Error al desvincular Microsoft:",
        error?.response?.data || error,
      );
    } finally {
      setLoadingProvider(null);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (adminConsentModalState.isOpen) return;
      if (!panelRef.current) return;

      if (!panelRef.current.contains(event.target)) {
        setExpandedAccountId(null);
      }
    }

    if (expandedAccountId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expandedAccountId, adminConsentModalState.isOpen]);

  const renderAccountEditor = (account, draft, isNew = false) => {
    const providerType = account?.oauth_account?.provider_type;
    const consentStatus = isNew ? null : consentStatusByAccount[account?.id];
    const consentNoticeClass =
      {
        APPROVED: styles.accountSuccess,
        PENDING: styles.accountNotice,
      }[consentStatus?.status] || styles.accountError;
    const accountUsers = isNew ? [] : (mailCorpUsers[account?.id] ?? null);
    const isCurrentUserMailAdmin = accountUsers
      ? accountUsers.some((a) => a.user_id === currentUser?.id && a.is_mail_account_admin)
      : false;

    const availableUserOptions = availableAccountUsers.map((availableUser) => ({
      value: availableUser.id,
      label:
        availableUser.first_name || availableUser.last_name
          ? `${availableUser.first_name ?? ""} ${availableUser.last_name ?? ""}`.trim()
          : availableUser.username,
    }));
    const selectedAvailableUserOption =
      availableUserOptions.find(
        (option) => option.value === newAssignmentDraft.userId,
      ) || null;

    return (
      <div
        ref={panelRef}
        className={styles.signaturePanel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.signatureHeader}>
          <div className={styles.signatureHeaderLeft}>
            <div className={styles.signatureAvatar}>
              {(
                (draft.name || draft.email || "+").charAt(0) || "+"
              ).toUpperCase()}
            </div>

            <div className={styles.signatureAccountInfo}>
              <div className={styles.signatureAccountName}>
                {isNew ? "Nueva cuenta" : account?.name || "Sin nombre"}
              </div>
              <div className={styles.signatureAccountEmail}>
                {draft.email || "-"}
              </div>
            </div>
          </div>

          <div className={styles.signatureActions}>
            <button
              type="button"
              className={styles.signatureSaveButton}
              onClick={
                isNew ? handleCreateAccount : () => handleSaveAccount(account)
              }
              disabled={isNew && creatingAccount}
            >
              {isNew && creatingAccount ? "Creando..." : "Guardar"}
            </button>
          </div>
        </div>

        {accountError && (
          <div className={styles.accountError} role="alert">
            {accountError}
          </div>
        )}

        <div className={styles.accountEditGrid}>
          <BaseInput
            label="Name"
            value={draft.name}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "name",
                value,
              )
            }
            required
          />

          <div className={styles.dropdownField}>
            <DropdownSelect
              title="Account"
              items={accountOptions}
              selectedItem={draft.account}
              onChange={(item) =>
                handleAccountSelect(
                  item,
                  isNew ? NEW_ACCOUNT_ID : account.id,
                  isNew,
                )
              }
              labelKey="name"
              isEditing
              error={draft.account?.id ? "" : "Account es obligatorio"}
            />
          </div>

          <BaseInput
            label="Email"
            type="email"
            value={draft.email}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "email",
                value,
              )
            }
            required
          />

          <BaseInput
            label="Password"
            type="password"
            value={draft.password}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "password",
                value,
              )
            }
            validateOnChange={false}
          />

          <BaseInput
            label="SMTP"
            value={draft.smtp}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "smtp",
                value,
              )
            }
          />

          <BaseInput
            label="SMTP Port"
            type="number"
            value={draft.smtp_port}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "smtp_port",
                value,
              )
            }
          />

          <BaseInput
            label="IMAP"
            value={draft.imap}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "imap",
                value,
              )
            }
          />

          <BaseInput
            label="IMAP Port"
            type="number"
            value={draft.imap_port}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "imap_port",
                value,
              )
            }
          />
        </div>

        <div className={styles.dropdownField}>
          <TimezoneSelect
            value={draft.timezone}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "timezone",
                value,
              )
            }
            required
            error={
              showHoursErrors && !draft.timezone
                ? "La zona horaria es obligatoria"
                : undefined
            }
          />
        </div>

        <div className={styles.businessHoursBlock}>
          <div className={styles.formLabel}>Horarios laborales</div>
          <BusinessHoursEditor
            value={draft.business_hours || []}
            onChange={(rows) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "business_hours",
                rows,
              )
            }
            errors={
              showHoursErrors
                ? validateBusinessHours(draft.business_hours || []).rowErrors
                : undefined
            }
          />
        </div>

        <div className={styles.toggleRowInline}>
          <span className={styles.toggleRowLabel}>Usar Bitrix</span>
          <Toggle
            checked={draft.use_bitrix ?? false}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "use_bitrix",
                value,
              )
            }
            ariaLabel="Usar Bitrix"
          />
        </div>

        {isNew &&
          (() => {
            const missingMessage = missingFieldsToLink(draft);

            return (
              <div className={styles.oauthSection}>
                <div className={styles.oauthTitle}>Conectar cuenta</div>

                <div className={styles.accountNotice} role="status">
                  {missingMessage ||
                    "Al vincular se guarda la casilla y se abre el proveedor para elegir la cuenta."}
                </div>

                <div className={styles.oauthButtons}>
                  <span title={missingMessage}>
                    <Button
                      variant="secondary"
                      tone={missingMessage ? "neutral" : "blue"}
                      iconLeft={
                        <img
                          src={googleLogo}
                          alt="Google"
                          className={missingMessage ? styles.mutedProviderLogo : undefined}
                        />
                      }
                      loading={creatingAccount}
                      disabled={Boolean(missingMessage)}
                      onClick={() => handleCreateAndLink("google")}
                    >
                      Guardar y vincular con Google
                    </Button>
                  </span>
                  <span title={missingMessage}>
                    <Button
                      variant="secondary"
                      tone={missingMessage ? "neutral" : "blue"}
                      iconLeft={
                        <img
                          src={microsoftLogo}
                          alt="Microsoft"
                          className={missingMessage ? styles.mutedProviderLogo : undefined}
                        />
                      }
                      loading={creatingAccount}
                      disabled={Boolean(missingMessage)}
                      onClick={() => handleCreateAndLink("microsoft")}
                    >
                      Guardar y vincular con Microsoft
                    </Button>
                  </span>
                </div>
              </div>
            );
          })()}

        {!isNew && (
          <div className={styles.oauthSection}>
            <div className={styles.oauthTitle}>Conectar cuenta</div>

            {consentStatus?.exists && !providerType && (
              <div className={consentNoticeClass} role="status">
                {consentStatus.status === "PENDING" && (
                  <>
                    ⚠ Esperando autorización administrativa.
                    {consentStatus.admin_email_masked
                      ? ` Solicitud enviada a ${consentStatus.admin_email_masked}.`
                      : ""}
                  </>
                )}
                {consentStatus.status === "APPROVED" && (
                  <>✓ La organización ya autorizó MaqMailing. Podés finalizar la conexión con Microsoft.</>
                )}
                {consentStatus.status === "REJECTED" && (
                  <>La autorización administrativa fue rechazada o cancelada.</>
                )}
                {consentStatus.status === "EXPIRED" && (
                  <>La solicitud administrativa expiró. Enviá una nueva solicitud.</>
                )}
                {consentStatus.status === "ERROR" && (
                  <>La solicitud administrativa terminó con error. Probá reenviar la solicitud.</>
                )}

                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <Button
                    variant="secondary"
                    tone="blue"
                    loading={refreshingConsentStatusId === account.id}
                    onClick={() => handleRefreshConsentStatus(account.id)}
                  >
                    Actualizar estado ahora
                  </Button>

                  {consentStatus.manual_action_required && consentStatus.review_url && (
                    <>
                      <Button
                        variant="secondary"
                        tone="blue"
                        onClick={() => handleCopyReviewUrl(account.id)}
                      >
                        Copiar link de aprobación
                      </Button>
                      <Button
                        variant="secondary"
                        tone="blue"
                        onClick={() => window.open(consentStatus.review_url, "_blank", "noopener,noreferrer")}
                      >
                        Abrir link
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className={styles.oauthButtons}>
              {!providerType && (
                <>
                  <Button
                    variant="secondary"
                    tone="blue"
                    iconLeft={<img src={googleLogo} alt="Google" />}
                    loading={loadingProvider === `google-${account.id}`}
                    onClick={() => handleLinkGoogle(account)}
                  >
                    Vincular con Google
                  </Button>

                  <Button
                    variant="secondary"
                    tone="blue"
                    iconLeft={<img src={microsoftLogo} alt="Microsoft" />}
                    loading={loadingProvider === `microsoft-${account.id}`}
                    onClick={() => handleLinkMicrosoft(account)}
                  >
                    Vincular con Microsoft
                  </Button>

                  <Button
                    variant="secondary"
                    tone="blue"
                    loading={requestingAdminConsentId === account.id}
                    onClick={() => openAdminConsentModal(account.id)}
                  >
                    Solicitar aprobación
                  </Button>
                </>
              )}

              {providerType === "google" && (
                <Button
                  variant="secondary"
                  tone="red"
                  iconLeft={<img src={googleLogo} alt="Google" />}
                  loading={loadingProvider === `unlink-google-${account.id}`}
                  onClick={() => handleUnlinkGoogle(account)}
                >
                  Desvincular Google
                </Button>
              )}

              {providerType === "microsoft" && (
                <Button
                  variant="secondary"
                  tone="red"
                  iconLeft={<img src={microsoftLogo} alt="Microsoft" />}
                  loading={loadingProvider === `unlink-microsoft-${account.id}`}
                  onClick={() => handleUnlinkMicrosoft(account)}
                >
                  Desvincular Microsoft
                </Button>
              )}
            </div>
          </div>
        )}

        {!isNew && whatsappEnabledFor(account.account?.id ?? account.account_id ?? account.account) && (
          <MailboxWhatsApp
            mailAccount={account}
            connections={mailboxWhatsApp.connectionsOf(account.id)}
            canManage={mailboxWhatsApp.administers(account.id)}
            loadError={mailboxWhatsApp.loadError}
            onChanged={mailboxWhatsApp.reload}
          />
        )}

        <div className={styles.signatureEditorBlock}>
          <div className={styles.formLabel}>Firma</div>
          <RichTextEditor
            value={draft.firma}
            onChange={(value) =>
              handleFieldChange(
                isNew ? NEW_ACCOUNT_ID : account.id,
                "firma",
                value,
              )
            }
            placeholder="Escribí la firma..."
            minHeight={220}
          />
        </div>
        {!isNew && isCurrentUserMailAdmin && (
          <div className={styles.mailCorpUsersSection}>
            <div className={styles.mailCorpUsersHeader}>
              <span className={styles.oauthTitle}>Usuarios con acceso</span>
              {addingUserMailCorpId !== account?.id && (
                <button
                  type="button"
                  className={styles.addUserButton}
                  onClick={() => handleOpenAddUser(account)}
                >
                  + Agregar usuario
                </button>
              )}
            </div>

            {accountUsers === null ? (
              <div className={styles.mailCorpUsersLoading}>Cargando...</div>
            ) : (
              <>
                {accountUsers.length === 0 && addingUserMailCorpId !== account?.id && (
                  <div className={styles.mailCorpUsersEmpty}>Sin usuarios asignados</div>
                )}
                {accountUsers.length > 0 && (
                  <ul className={styles.mailCorpUsersList}>
                    {accountUsers.map((assignment) => {
                      const displayName = assignment.full_name || assignment.username || "?";
                      const initial = displayName.charAt(0).toUpperCase();
                      return (
                        <li key={assignment.id} className={styles.mailCorpUserItem}>
                          <div className={styles.mailCorpUserAvatar}>{initial}</div>
                          <div className={styles.mailCorpUserInfo}>
                            <span className={styles.mailCorpUserName}>{displayName}</span>
                            <span className={styles.mailCorpUserEmail}>{assignment.email}</span>
                          </div>
                          <div className={styles.mailCorpUserFlags}>
                            <div className={styles.flagItem}>
                              <span className={styles.flagItemLabel}>Admin</span>
                              <Toggle
                                checked={assignment.is_mail_account_admin}
                                onChange={() =>
                                  handleToggleAssignmentFlag(account.id, assignment.id, "is_mail_account_admin", assignment.is_mail_account_admin)
                                }
                                ariaLabel="Mail account admin"
                              />
                            </div>
                            <div className={styles.flagItem}>
                              <span className={styles.flagItemLabel}>Enviar</span>
                              <Toggle
                                checked={assignment.can_send}
                                onChange={() =>
                                  handleToggleAssignmentFlag(account.id, assignment.id, "can_send", assignment.can_send)
                                }
                                ariaLabel="Puede enviar"
                              />
                            </div>
                            <div className={styles.flagItem}>
                              <span className={styles.flagItemLabel}>Recibir</span>
                              <Toggle
                                checked={assignment.can_receive}
                                onChange={() =>
                                  handleToggleAssignmentFlag(account.id, assignment.id, "can_receive", assignment.can_receive)
                                }
                                ariaLabel="Puede recibir"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            className={styles.removeUserButton}
                            onClick={() => handleRemoveUser(account.id, assignment.id)}
                            title="Quitar usuario"
                          >
                            <TrashIcon />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {addingUserMailCorpId === account?.id && (
                  <div className={styles.addUserForm}>
                    {loadingAvailableUsers ? (
                      <div className={styles.mailCorpUsersLoading}>Cargando usuarios disponibles...</div>
                    ) : (
                      <>
                        <DropdownSelect
                          placeholder="Seleccioná un usuario..."
                          items={availableUserOptions}
                          selectedItem={selectedAvailableUserOption}
                          labelKey="label"
                          valueKey="value"
                          isEditing
                          onChange={(selectedOption) =>
                            setNewAssignmentDraft((prev) => ({
                              ...prev,
                              userId: selectedOption ? Number(selectedOption.value) : null,
                            }))
                          }
                        />
                        <div className={styles.addUserFlags}>
                          <div className={styles.flagItem}>
                            <span className={styles.flagItemLabel}>Admin</span>
                            <Toggle
                              checked={newAssignmentDraft.isMailAccountAdmin}
                              onChange={(value) =>
                                setNewAssignmentDraft((prev) => ({ ...prev, isMailAccountAdmin: value }))
                              }
                              ariaLabel="Mail account admin"
                            />
                          </div>
                          <div className={styles.flagItem}>
                            <span className={styles.flagItemLabel}>Enviar</span>
                            <Toggle
                              checked={newAssignmentDraft.canSend}
                              onChange={(value) =>
                                setNewAssignmentDraft((prev) => ({ ...prev, canSend: value }))
                              }
                              ariaLabel="Puede enviar"
                            />
                          </div>
                          <div className={styles.flagItem}>
                            <span className={styles.flagItemLabel}>Recibir</span>
                            <Toggle
                              checked={newAssignmentDraft.canReceive}
                              onChange={(value) =>
                                setNewAssignmentDraft((prev) => ({ ...prev, canReceive: value }))
                              }
                              ariaLabel="Puede recibir"
                            />
                          </div>
                        </div>
                        <div className={styles.addUserActions}>
                          <button
                            type="button"
                            className={styles.addUserCancelButton}
                            onClick={() => setAddingUserMailCorpId(null)}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className={styles.addUserConfirmButton}
                            disabled={!newAssignmentDraft.userId || savingAssignment}
                            onClick={() => handleAddUser(account)}
                          >
                            {savingAssignment ? "Agregando..." : "Agregar"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={styles.sectionContent}>
        {!accounts.length ? (
          <div className={styles.emptyState}>
            No hay cuentas linkeadas para mostrar.
          </div>
        ) : null}

        <div className={styles.accountsList}>
          {accounts.map((account) => {
            const isExpanded = expandedAccountId === account.id;
            const draft = drafts[account.id] || buildDraftFromAccount(account);
            const whatsAppNumbers = mailboxWhatsApp
              .connectionsOf(account.id)
              .map((connection) => connection.display_phone_number || connection.phone_number_id)
              .join(", ");

            return (
              <div
                key={account.id || account.email}
                className={styles.accountItem}
              >
                {isExpanded ? (
                  renderAccountEditor(account, draft, false)
                ) : (
                  <button
                    type="button"
                    className={styles.accountCardButton}
                    onClick={() => handleToggleEditor(account)}
                  >
                    <div className={styles.accountCard}>
                      <div className={styles.accountLeft}>
                        <div className={styles.accountLogo}>
                          <span>
                            {(account.name || account.email || "?")
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        </div>

                        <div className={styles.accountInfo}>
                          <div className={styles.accountTopLine}>
                            <span className={styles.accountCompany}>
                              {account.name || "Sin nombre"}
                            </span>
                            {whatsAppNumbers && (
                              <WhatsAppIcon
                                className={styles.accountWhatsApp}
                                role="img"
                                aria-label={`WhatsApp conectado: ${whatsAppNumbers}`}
                                title={`WhatsApp: ${whatsAppNumbers}`}
                              />
                            )}
                          </div>

                          <div className={styles.accountEmail}>
                            {account.email || "-"}
                          </div>
                        </div>
                      </div>

                      <span className={styles.linkButton}>Editar cuenta</span>
                    </div>
                  </button>
                )}
              </div>
            );
          })}

          <div className={styles.addAccountItem}>
            {expandedAccountId === NEW_ACCOUNT_ID ? (
              renderAccountEditor(
                null,
                drafts[NEW_ACCOUNT_ID] || buildNewDraft(),
                true,
              )
            ) : (
              <button
                type="button"
                className={styles.addAccountButton}
                onClick={handleOpenCreateAccount}
              >
                <span className={styles.addAccountPlus}>+</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <AccountActionModal
        isOpen={adminConsentModalState.isOpen}
        title="Solicitar aprobación administrativa (Microsoft)"
        fields={[
          {
            name: "admin_email",
            label: "Email del administrador",
            type: "email",
            value: currentUser?.email || "",
            required: true,
            requiredMessage: "El email del administrador es obligatorio",
            placeholder: "admin@empresa.com",
          },
        ]}
        submitText={
          requestingAdminConsentId === adminConsentModalState.accountId
            ? "Enviando..."
            : "Enviar solicitud"
        }
        onCancel={closeAdminConsentModal}
        onSubmit={handleSubmitAdminConsent}
      />
    </>
  );
};

export default LinkedAccountsSection;
